import getSymbolFromCurrency from 'currency-symbol-map';
import produce, { current } from 'immer';
import {
  first,
  forEach,
  isEmpty,
  isUndefined,
  last,
  snakeCase,
  trim,
} from 'lodash-es';

import { CommunityRoles } from '@/utils/communityRoles';
import { authorizedServerFetch } from 'actions/server.actions';
import OrgType from 'constants/orgType.constant';
import { isValidDate } from '../utils/dateHelpers';
import getQualifiedImageUrl from '../utils/imageUrlProcessor';
import { checkForSubscriptionAlert } from './alertActions';
import useStore from './store';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import { getUserCountry } from '@/utils/user.helper';

const isProfileIncomplete = (
  profile,
  { isNonProfitUser, isNonProfitAdmin },
) => {
  const mandatoryProfileFields = [
    'firstName',
    'lastName',
    'email',
    'designation',
    't_and_c_timestamp',
  ];
  const hasCauses = !isEmpty(profile.causes) || isNonProfitUser;
  const hasSkills = !isEmpty(profile.skills) || isNonProfitAdmin;
  return (
    mandatoryProfileFields.some((field) => isEmpty(trim(profile[field]))) ||
    !hasCauses ||
    !hasSkills
  );
};

const formatProfile = (profile, dataFromServer) => {
  const formatted = {};
  forEach(profile, (value, key) => {
    const snakeCaseKey = snakeCase(key);
    if (key === 'avatar') {
      formatted[key] = getQualifiedImageUrl(dataFromServer[key]);
    } else if (key === 'bannerImage') {
      formatted[key] = getQualifiedImageUrl(dataFromServer[key]);
    } else if (
      !isUndefined(dataFromServer[key]) ||
      !isUndefined(dataFromServer[snakeCaseKey])
    ) {
      formatted[key] = isUndefined(dataFromServer[key])
        ? dataFromServer[snakeCase(key)]
        : dataFromServer[key];
    }
  });

  forEach(dataFromServer, (value, key) => {
    const snakeCaseKey = snakeCase(key);
    if (key === snakeCaseKey) {
      formatted[key] = value;
    }
  });

  const profileData = { ...profile, ...formatted };

  if (profileData?.location) {
    const updatedLocation = { ...profileData.location };
    // updatedLocation.country = 'IN';
    updatedLocation.currencySymbol = getSymbolFromCurrency(
      profileData?.location?.currency,
    );

    // Assign the updated location object back to profileData
    profileData.location = updatedLocation;
  }

  // if (profileData?.location) {
  //   profileData.location.currencySymbol = getSymbolFromCurrency(
  //     profileData?.location?.currency
  //   );
  //   // we could use the following line once the bug is fixed https://github.com/AlexDid/iso-country-currency/issues/17
  //   // profileData.location.currencySymbol = getParamByParam('currency', profileData.location.currency, 'symbol');
  // }

  // ensure date is valid
  if (profileData.dob) {
    const dob = new Date(profileData.dob);
    if (!isValidDate(dob)) {
      profileData.dob = null;
    }
  }

  if (!isEmpty(profileData?.company?.reward?.levels)) {
    profileData.company = {
      ...profileData.company,
      reward: {
        ...profileData.company.reward,
        levels: profileData.company?.reward?.levels.map((level, index) => ({
          ...level,
          index: index,
        })),
      },
    };
  }

  if (!isEmpty(profileData?.activeCommunity?.reward?.levels)) {
    profileData.activeCommunity = {
      ...profileData.activeCommunity,
      reward: {
        ...profileData.activeCommunity.reward,
        levels: profileData.activeCommunity?.reward?.levels.map(
          (level, index) => ({
            ...level,
            index: index,
          }),
        ),
      },
    };
  }

  // TODO: check - restricted donationFeatures for non us orgs

  if (
    profileData?.location?.country !== 'US' &&
    !isEmpty(profileData?.company?.subscription?.allowedFeatures)
  ) {
    const donationFeatures = ['employerMatching', 'donation'];

    const updatedAllowedFeatures =
      profileData.company.subscription.allowedFeatures.filter(
        (f) => !donationFeatures.includes(f),
      );

    // Create a new object structure without mutating original
    const updatedProfileData = {
      ...profileData,
      company: {
        ...profileData.company,
        subscription: {
          ...profileData.company.subscription,
          allowedFeatures: updatedAllowedFeatures,
        },
      },
    };

    // Now use updatedProfileData instead of mutating profileData
    return updatedProfileData;
  }

  // TODO: check - restricted donationFeatures for non us orgs
  if (
    profileData?.location?.country !== 'US' &&
    !isEmpty(profileData?.activeCommunity?.subscription?.allowedFeatures)
  ) {
    // TODO: check - removed 'raiseFund'
    // const donationFeatures = ['employerMatching', 'donation', 'raiseFund', 'p2pRaiseFund'];
    const donationFeatures = ['employerMatching', 'donation'];
    const updatedAllowedFeatures =
      profileData.activeCommunity.subscription.allowedFeatures.filter(
        (f) => !donationFeatures.includes(f),
      );

    // Create a new object with the updated allowedFeatures
    profileData.activeCommunity.subscription = {
      ...profileData.activeCommunity.subscription,
      allowedFeatures: updatedAllowedFeatures,
    };
  }

  // if (
  //   profileData?.location?.country !== 'US' &&
  //   !isEmpty(profileData.company.subscription?.allowedFeatures)
  // ) {
  //   const donationFeatures = ['employerMatching', 'donation', 'raiseFund'];
  //   profileData.company.subscription.allowedFeatures =
  //     profileData.company.subscription.allowedFeatures.filter(
  //       (f) => !donationFeatures.includes(f)
  //     );
  // }

  return profileData;
};

const formatExternalProfile = (profile, dataFromServer) => {
  const formatted = {};
  forEach(profile, (value, key) => {
    const snakeCaseKey = snakeCase(key);
    if (
      !isUndefined(dataFromServer[key]) ||
      !isUndefined(dataFromServer[snakeCaseKey])
    ) {
      formatted[key] = isUndefined(dataFromServer[key])
        ? dataFromServer[snakeCase(key)]
        : dataFromServer[key];
    }
  });
  return { ...formatted };
};

const checkIsNekiAdmin = (user) => {
  return user?.isNekiAdmin || false;
};
const checkIsCommunityCompanyAdmin = (user) => {
  return (
    user.activeCommunity?.role === CommunityRoles.Admin &&
    [OrgType.Business, OrgType.ChangeMakers].includes(
      user.activeCommunity?.orgType,
    )
  );
};

const checkIsCommunityNonProfitAdmin = (user) => {
  return (
    user.activeCommunity?.role === CommunityRoles.Admin &&
    user.activeCommunity?.orgType === OrgType.NonProfit
  );
};
const checkIsCommunityCompanyUser = (user) => {
  return (
    user.activeCommunity?.role === CommunityRoles.Member &&
    [OrgType.Business, OrgType.ChangeMakers].includes(
      user.activeCommunity?.orgType,
    )
  );
};

const checkIsCommunityNonProfitUser = (user) => {
  return (
    user.activeCommunity?.role === CommunityRoles.Member &&
    user.activeCommunity?.orgType === OrgType.NonProfit
  );
};

const isCurrentUser = (user) => {
  const userData = useStore.getState().user.profile;
  return userData.id === user.id;
};

const dispatchGetUserProfile = async (user, refresh = false) => {
  console.log('Loading user profile');
  console.log(user);

  let userData = user;
  if (refresh) {
    const { data, ok } = await authorizedServerFetch('users/me/profile');
    console.log('data', data);
    userData = data?.user;
  }
  updateUserProfile(userData);
  return userData;
};

const updateUserProfile = (profile) => {
  useStore.setState(
    produce((draft) => {
      draft.user.roles = [
        profile.activeCommunity?.role || CommunityRoles.Member,
      ];
      draft.user.profile = formatProfile(current(draft.user.profile), profile);
      // TODO: check
      // draft.user.isNekiAdmin = checkIsNekiAdmin(draft.user.profile);
      draft.user.isNekiAdmin = checkIsNekiAdmin(profile || draft.user.profile);
      draft.user.isCompanyAdmin = checkIsCommunityCompanyAdmin(
        draft.user.profile,
      );
      draft.user.isNonProfitAdmin = checkIsCommunityNonProfitAdmin(
        draft.user.profile,
      );
      draft.user.isCompanyUser = checkIsCommunityCompanyUser(
        draft.user.profile,
      );
      draft.user.isNonProfitUser = checkIsCommunityNonProfitUser(
        draft.user.profile,
      );
      draft.user.isNonProfitUser =
        draft.user.profile.activeCommunity.orgType === OrgType.NonProfit;
      draft.user.isProfileIncomplete = isProfileIncomplete(
        draft.user.profile,
        draft.user,
      );
      draft.user.isOrgAdmin =
        draft.user.isCompanyAdmin || draft.user.isNonProfitAdmin;

      if (draft.user.isNonProfitAdmin) {
        draft.user.profile.selectedProfile = 'org';
        draft.user.profile.displayName =
          draft.user.profile.activeCommunity.name;
      } else {
        draft.user.profile.selectedProfile = 'me';
        draft.user.profile.displayName = draft.user.profile.fullName;
      }

      draft.user.isProfileLoading = false;
    }),
  );
  checkForSubscriptionAlert(
    useStore.getState().user?.profile?.activeCommunity?.subscription,
  );
  console.log('User profile loaded ', profile);
};

const updatePoints = (reward) => {
  useStore.setState(
    produce((draft) => {
      draft.user.profile.points = reward?.points.total;
      draft.user.profile.badgesCount = reward?.badges.total;
    }),
  );
};

// TODO: Review by Shyam
const isCompanyFollowingNonProfit = (nonProfitId) => {
  const { profile } = useStore.getState().user;
  let isFollowing = false;
  if (
    profile?.activeCommunity?.following?.nonprofits?.includes(nonProfitId) ||
    profile?.company?.following?.nonprofits?.includes(nonProfitId)
  ) {
    isFollowing = true;
  }

  // if (profile?.activeCommunity?.following?.nonprofits?.includes(nonProfitId)) {
  //   isFollowing = true;
  // }

  return isFollowing;
};

const isFollowingNonProfit = (nonProfitId, excludeCompanyFollowing) => {
  const user = useStore.getState().user.profile;
  let isFollowing = false;

  let isCompanyFollowing = isCompanyFollowingNonProfit(nonProfitId); // TODO: check
  if (excludeCompanyFollowing) {
    isCompanyFollowing = false;
  }
  if (user.following?.nonprofits?.includes(nonProfitId) || isCompanyFollowing) {
    isFollowing = true;
  }

  // if (
  //   user.following?.nonprofits?.find((n) => n.id === nonProfitId) ||
  //   isCompanyFollowingNonProfit(nonProfitId)
  // ) {
  //   isFollowing = true;
  // }

  return isFollowing;
};

const isFollowingUser = (userId) => {
  const user = useStore.getState().user.profile;
  let isFollowing = false;
  if (user?.following?.influencers?.includes(userId)) {
    isFollowing = true;
  }
  return userId === user.id ? false : isFollowing;
};

const onNonProfitFollowed = (nonProfitId) => {
  useStore.setState(
    produce((draft) => {
      draft.user.profile.following.nonprofits = [
        ...(draft.user.profile?.following?.nonprofits || []),
        nonProfitId,
      ];
      const cachedNonProfit = draft.nonProfit.cached[nonProfitId];
      if (cachedNonProfit) {
        cachedNonProfit.followers = [
          ...(cachedNonProfit.followers || []),
          draft.user.profile.id,
        ];
        cachedNonProfit.numFollowers += 1;
      }
    }),
  );
};

const onNonProfitUnFollowed = (nonProfitId) => {
  useStore.setState(
    produce((draft) => {
      if (!isEmpty(draft.user.profile?.following?.nonprofits)) {
        draft.user.profile.following.nonprofits =
          draft.user.profile.following.nonprofits.filter(
            (id) => id !== nonProfitId,
          );
      }

      const cachedNonProfit = draft.nonProfit.cached[nonProfitId];
      if (cachedNonProfit) {
        if (!isEmpty(cachedNonProfit.followers)) {
          cachedNonProfit.followers = cachedNonProfit.followers.filter(
            (id) => id !== draft.user.profile.id,
          );
        }
        cachedNonProfit.numFollowers = Math.max(
          cachedNonProfit.numFollowers - 1,
          0,
        );
      }
    }),
  );
};

const toggleFollowNonProfit = async (nonProfitId) => {
  const isFollowing = isFollowingNonProfit(nonProfitId);
  const action = isFollowing ? 'unfollow' : 'follow';
  const path = `user-actions/${action}/nonprofits/${nonProfitId}`;

  // first optimistic update of local state
  if (action === 'follow') {
    onNonProfitFollowed(nonProfitId);
  } else {
    onNonProfitUnFollowed(nonProfitId);
  }

  const { data, ok } = await authorizedServerFetch(path, {
    method: 'PUT',
    body: {},
  });
  if (!ok || !data) {
    // revert optimistic update of local state
    if (action === 'follow') {
      onNonProfitUnFollowed(nonProfitId);
    } else {
      onNonProfitFollowed(nonProfitId);
    }
  } else {
    const { following: followingNonProfitIds, points } = data.data;
    // @ts-ignore
    updatePoints(points?.total, points?.badges);
    // update local state
    useStore.setState(
      produce((draft) => {
        draft.user.profile.following.nonprofits = followingNonProfitIds;
      }),
    );
  }

  return isFollowingNonProfit(nonProfitId);
};

const toggleFollowInfluencer = async (userId) => {
  const isFollowing = isFollowingUser(userId);
  const action = isFollowing ? 'unfollow' : 'follow';
  const path = `user-actions/${action}/influencers/${userId}`;
  const incrementValue = action === 'follow' ? 1 : -1;
  const { data, ok } = await fetchViaProxy(`/proxy/v2/protected/${path}`, {
    method: 'PUT',
  });

  if (ok) {
    const followingUserIds = data?.data;
    useStore.setState(
      produce((draft) => {
        draft.user.profile.following.influencers = followingUserIds;
        forEach(draft.socialFeed.allFeedItems, (post) => {
          const { author } = post;
          if (author && author.id === userId) {
            author.numFollowers += incrementValue;
          }
        });
      }),
    );
  }
  return isFollowingUser(userId);
};

const hasAppAccess = (appName, user = null) => {
  const userProfile = user || useStore.getState().user.profile;

  if (userProfile?.activeCommunity?.subscription) {
    return userProfile?.activeCommunity?.subscription?.allowedFeatures?.includes(
      appName,
    );
  }
};

const useGetCurrentUserLevel = () => {
  const currentLevelId = useStore(
    (state) => state.user.profile?.currentLevelId,
  );
  const levels =
    useStore((state) => state.user.profile?.activeCommunity?.reward?.levels) ||
    [];
  return levels.find((l) => l.id === currentLevelId) || first(levels);
  // const [currentLevel, setCurrentLevel] = useState(
  //   first(useStore.getState().user.profile?.activeCommunity?.reward?.levels),
  // );
  // useEffect(() => {
  //   const { levels = [] } =
  //     useStore.getState().user.profile?.activeCommunity?.reward;
  //   const level = levels.find((l) => l.id === currentLevelId) || first(levels);
  //   setCurrentLevel(level);
  // }, [currentLevelId]);
  // return currentLevel;
};

const useGetNextUserLevel = () => {
  const currentLevelId = useStore(
    (state) => state.user.profile?.currentLevelId,
  );
  const levels =
    useStore((state) => state.user.profile?.activeCommunity?.reward?.levels) ||
    [];

  const levelIndex = levels.findIndex((l) => l.id === currentLevelId);
  if (levelIndex === -1) {
    return first(levels);
  } else if (levelIndex === levels.length - 1) {
    return last(levels);
  } else {
    return levels[levelIndex + 1];
  }

  // const [nextLevel, setNextLevel] = useState(
  //   first(useStore.getState().user.profile?.activeCommunity?.reward?.levels),
  // );
  // useEffect(() => {
  //   const { levels = [] } =
  //     useStore.getState().user.profile?.activeCommunity?.reward;
  //   const levelIndex = levels.findIndex((l) => l.id === currentLevelId);
  //   if (levelIndex === -1) {
  //     setNextLevel(first(levels));
  //   } else if (levelIndex === levels.length - 1) {
  //     setNextLevel(last(levels));
  //   } else {
  //     setNextLevel(levels[levelIndex + 1]);
  //   }
  // }, [currentLevelId]);
  // return nextLevel;
};

const currentLevel = () => {
  const { profile } = useStore.getState().user;
  const { levels = [] } = profile?.activeCommunity?.reward;
  return levels.find((l) => l.id === profile.currentLevelId) || first(levels);
};

const nextLevel = () => {
  const { profile } = useStore.getState().user;
  const { levels = [] } = profile?.activeCommunity?.reward;
  const levelIndex = levels.findIndex((l) => l.id === profile.currentLevelId);
  let level = levels[levelIndex + 1];
  if (levelIndex === -1) {
    level = first(levels);
  } else if (levelIndex === levels.length - 1) {
    level = last(levels);
  }
  return level;
};

const canDonateToNonProfit = (nonProfit) => {
  if (nonProfit) {
    const userProfile = useStore.getState().user.profile;
    if (
      getUserCountry(userProfile) !== nonProfit.country &&
      nonProfit.allowForeignDonation === false
    ) {
      return {
        canDonate: false,
        reason: 'country',
        message: 'This non profit can not receive foreign donations',
      };
    }
  }
  return {
    canDonate: !!nonProfit,
    message: '',
    reason: '',
  };
};

const setSelectedProfile = (profile) => {
  useStore.setState(
    produce((draft) => {
      draft.user.profile.selectedProfile = profile;
    }),
  );
};

const uploadImage = async (imageFile, type) => {
  const path = `users/me/attachment/image?type=${type}&token=64b4bd2c68aa2f5b29c961be`;
  const formData = new FormData();
  formData.append('file', imageFile);

  const { data, ok } = await authorizedServerFetch(path, {
    method: 'POST',
    body: formData,
  });

  if (ok && data?.data) {
    return data?.data?.url;
  }
  return null;
};

const fetchCurrentUserBadges = async () => {
  const path = 'users/me/badges';
  const { data, ok } = await authorizedServerFetch(path);
  return data?.data;
};

const getUserProfileFromStore = () => {
  const profile = useStore.getState().user.profile;
  if (profile && profile.id > 0) {
    return profile;
  }
  return null;
};

export {
  canDonateToNonProfit,
  currentLevel,
  dispatchGetUserProfile,
  fetchCurrentUserBadges,
  formatExternalProfile,
  hasAppAccess,
  isCompanyFollowingNonProfit,
  isCurrentUser,
  isFollowingNonProfit,
  isFollowingUser,
  nextLevel,
  setSelectedProfile,
  toggleFollowInfluencer,
  toggleFollowNonProfit,
  updatePoints,
  updateUserProfile,
  uploadImage,
  useGetCurrentUserLevel,
  useGetNextUserLevel,
  getUserProfileFromStore,
};
